import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  GraduationCap, Briefcase, Star, Award, Globe,
  ShieldCheck, Mail, Linkedin, MapPin
} from 'lucide-react';
import './PublicCV.css';

export default function PublicCV() {
  const { username } = useParams();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchPublicCV = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cv/public/${username}`);
        if (res.ok) {
          const data = await res.json();
          setProfile(data.profile);
        } else {
          setError(isRTL ? 'الملف الشخصي غير موجود' : 'Profile not found');
        }
      } catch {
        setError(isRTL ? 'خطأ في الاتصال' : 'Connection error');
      }
      setLoading(false);
    };
    fetchPublicCV();
  }, [username, isRTL]);

  if (loading) return (
    <div className="public-cv-page" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="public-cv-container">
        <Skeleton className="h-32 w-full mb-4" />
        <Skeleton className="h-8 w-48 mb-2" />
        <Skeleton className="h-4 w-64 mb-6" />
        <Skeleton className="h-40 w-full" />
      </div>
    </div>
  );

  if (error) return (
    <div className="public-cv-page" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="public-cv-container">
        <div className="public-cv-error">{error}</div>
      </div>
    </div>
  );

  const p = profile.personal || {};
  const u = profile.user || {};

  return (
    <div className="public-cv-page" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="public-cv-container" data-testid="public-cv-container">
        {/* Header */}
        <header className="pcv-header">
          <div className="pcv-name-row">
            <h1 data-testid="pcv-name">{p.full_name || u.name}</h1>
            {u.student_verified && (
              <Badge variant="outline" className="pcv-verified">
                <ShieldCheck size={14} /> {isRTL ? 'طالب موثق' : 'Verified'}
              </Badge>
            )}
          </div>
          {u.university_name && (
            <p className="pcv-university">
              <GraduationCap size={15} />
              {isRTL ? u.university_name_ar || u.university_name : u.university_name}
            </p>
          )}
          <div className="pcv-contacts">
            {p.state_key && <span><MapPin size={13} /> {p.state_key}</span>}
            {p.linkedin && <a href={p.linkedin} target="_blank" rel="noopener noreferrer"><Linkedin size={13} /> LinkedIn</a>}
          </div>
          {u.average_rating > 0 && (
            <div className="pcv-rating">
              <Star size={14} fill="#F59E0B" stroke="#F59E0B" /> {u.average_rating.toFixed(1)}
            </div>
          )}
        </header>

        {/* Summary */}
        {profile.summary && (
          <section className="pcv-section">
            <h2>{isRTL ? 'الملخص' : 'Summary'}</h2>
            <p className="pcv-summary-text">{profile.summary}</p>
          </section>
        )}

        {/* Education */}
        {profile.education?.length > 0 && (
          <section className="pcv-section" data-testid="pcv-education">
            <h2><GraduationCap size={16} /> {isRTL ? 'التعليم' : 'Education'}</h2>
            <div className="pcv-entries">
              {profile.education.map((edu, i) => (
                <div key={i} className="pcv-entry">
                  <div className="pcv-entry-title">{edu.institution}</div>
                  <div className="pcv-entry-sub">
                    {edu.degree && <span>{edu.degree}</span>}
                    {edu.major && <span>{edu.major}</span>}
                    {(edu.start || edu.end) && <span className="pcv-dates">{edu.start} - {edu.end || (isRTL ? 'حالياً' : 'Present')}</span>}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Experience */}
        {(profile.employment?.length > 0 || profile.experience?.length > 0) && (
          <section className="pcv-section" data-testid="pcv-experience">
            <h2><Briefcase size={16} /> {isRTL ? 'الخبرة' : 'Experience'}</h2>
            <div className="pcv-entries">
              {(profile.employment || profile.experience || []).map((exp, i) => (
                <div key={i} className="pcv-entry">
                  <div className="pcv-entry-title">{exp.title || exp.position}</div>
                  <div className="pcv-entry-sub">
                    {exp.company && <span>{exp.company}</span>}
                    {(exp.start || exp.end) && <span className="pcv-dates">{exp.start} - {exp.end || (isRTL ? 'حالياً' : 'Present')}</span>}
                  </div>
                  {exp.description && <p className="pcv-entry-desc">{exp.description}</p>}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Skills */}
        {profile.skills?.length > 0 && (
          <section className="pcv-section" data-testid="pcv-skills">
            <h2><Star size={16} /> {isRTL ? 'المهارات' : 'Skills'}</h2>
            <div className="pcv-skills-grid">
              {profile.skills.map((skill, i) => (
                <Badge key={i} variant="secondary" className="pcv-skill-badge">{skill}</Badge>
              ))}
            </div>
          </section>
        )}

        {/* Certifications */}
        {profile.certifications?.length > 0 && (
          <section className="pcv-section">
            <h2><Award size={16} /> {isRTL ? 'الشهادات' : 'Certifications'}</h2>
            <div className="pcv-entries">
              {profile.certifications.map((cert, i) => (
                <div key={i} className="pcv-entry">
                  <div className="pcv-entry-title">{cert.name}</div>
                  {cert.issuer && <div className="pcv-entry-sub"><span>{cert.issuer}</span></div>}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Languages */}
        {profile.languages?.length > 0 && (
          <section className="pcv-section">
            <h2><Globe size={16} /> {isRTL ? 'اللغات' : 'Languages'}</h2>
            <div className="pcv-skills-grid">
              {profile.languages.map((lang, i) => {
                const name = typeof lang === 'string' ? lang : (lang.language || lang.name || '');
                const level = typeof lang === 'string' ? '' : (lang.level || '');
                if (!name && !level) return null;
                return (
                  <Badge key={i} variant="outline" className="pcv-lang-badge">
                    {name}{level ? ` — ${level}` : ''}
                  </Badge>
                );
              })}
            </div>
          </section>
        )}

        {/* Badges */}
        {u.badges?.length > 0 && (
          <section className="pcv-section">
            <h2>{isRTL ? 'الأوسمة' : 'Badges'}</h2>
            <div className="pcv-skills-grid">
              {u.badges.map((b, i) => <Badge key={i} className="pcv-badge-item">{b}</Badge>)}
            </div>
          </section>
        )}

        <footer className="pcv-footer">
          <span>{isRTL ? 'تم إنشاؤه بواسطة واصلة' : 'Built with Wassla'}</span>
        </footer>
      </div>
    </div>
  );
}
